import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Download } from "lucide-react";
import { Reveal } from "./Reveal";
import { TRAINING } from "../../data/content";

const BROCHURE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/brochure`;

export const Training = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="training" className="relative py-24 md:py-40 border-t border-brand-line" data-testid="training-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="overline mb-6">03 — Specialized training</p>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-brand-text leading-[1.02]">
              Six disciplines. 120 programmes.
            </h2>
          </div>
          <div className="md:max-w-xs">
            <p className="text-brand-soft font-light">
              A curated curriculum of 20 courses within each specialized area — designed for measurable, real-world impact.
            </p>
            <a
              href={BROCHURE_URL}
              download="Peil-Consultancy-Training-Catalogue.pdf"
              data-testid="download-brochure"
              className="group mt-6 inline-flex items-center gap-3 px-6 py-3.5 bg-brand text-brand-base font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors duration-300"
            >
              <Download size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
              Download Catalogue (PDF)
            </a>
          </div>
        </Reveal>

        <div className="border-t border-brand-line" data-testid="training-accordion">
          {TRAINING.map((area, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={area.title} delay={i * 0.04}>
                <div className="border-b border-brand-line">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`training-toggle-${i}`}
                    aria-expanded={isOpen}
                    className="w-full group flex items-center gap-5 md:gap-8 py-7 md:py-9 text-left"
                  >
                    <span className="font-mono text-xs text-brand-muted w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 flex flex-col md:flex-row md:items-baseline md:gap-5">
                      <h3
                        className={`font-display text-2xl md:text-4xl lg:text-5xl font-light transition-colors duration-400 ${
                          isOpen ? "text-brand" : "text-brand-text group-hover:text-brand"
                        }`}
                      >
                        {area.title}
                      </h3>
                      <span className="mt-1 md:mt-0 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">
                        {area.tag} · 20 courses
                      </span>
                    </span>
                    <span
                      className={`grid place-items-center w-10 h-10 rounded-full border shrink-0 transition-all duration-400 ${
                        isOpen
                          ? "border-brand text-brand rotate-45 bg-brand/5"
                          : "border-brand-line text-brand-soft group-hover:border-brand group-hover:text-brand"
                      }`}
                    >
                      <Plus size={18} />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <ol
                          data-testid={`training-courses-${i}`}
                          className="md:pl-16 pb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-1"
                        >
                          {area.courses.map((c, ci) => (
                            <motion.li
                              key={c}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 + ci * 0.015, duration: 0.4 }}
                              className="group flex items-baseline gap-3 py-2.5 border-b border-brand-line/60"
                            >
                              <span className="font-mono text-[10px] text-brand/70 shrink-0 w-5">
                                {String(ci + 1).padStart(2, "0")}
                              </span>
                              <span className="text-brand-soft text-sm font-light leading-snug group-hover:text-brand-text transition-colors duration-300">
                                {c}
                              </span>
                            </motion.li>
                          ))}
                        </ol>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Training;
